/**
 * Presence — online / last-seen status for landlords and tenants.
 *
 * - useHeartbeat()           : call once near the top of an authenticated tree.
 *                              Pings the heartbeat() RPC on mount and every
 *                              ~60s while the tab is visible.
 * - usePresenceFor(userIds)  : returns a Map<userId, last_seen_at> for the
 *                              given users. Refreshes every 30s + on focus.
 *                              Server scopes results to people the caller
 *                              has a tenant_link relationship with — anyone
 *                              else just won't appear in the result.
 * - formatPresence(date)     : "Online" | "Last seen 5m ago" | …
 * - presenceTone(date)       : 'online' | 'recent' | 'idle' | 'offline'
 */
import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';

const HEARTBEAT_INTERVAL_MS = 60_000;   // 60s
const PRESENCE_REFRESH_MS   = 30_000;   // 30s
const ONLINE_WINDOW_MS      = 90_000;   // counted "online" if last_seen ≤ 90s ago

// ─── Heartbeat ───────────────────────────────────────────────────────────────
export function useHeartbeat() {
    useEffect(() => {
        let cancelled = false;
        let intervalId: number | null = null;

        const ping = async () => {
            if (cancelled) return;
            if (document.visibilityState !== 'visible') return;
            try {
                await supabase.rpc('heartbeat');
            } catch {
                // Silently ignore; presence is best-effort.
            }
        };

        const start = () => {
            if (intervalId !== null) return;
            ping();
            intervalId = window.setInterval(ping, HEARTBEAT_INTERVAL_MS);
        };
        const stop = () => {
            if (intervalId !== null) {
                window.clearInterval(intervalId);
                intervalId = null;
            }
        };

        // Restart cycle when tab visibility changes
        const onVis = () => {
            if (document.visibilityState === 'visible') {
                start();
            } else {
                stop();
            }
        };

        if (document.visibilityState === 'visible') start();
        document.addEventListener('visibilitychange', onVis);
        window.addEventListener('focus', ping);

        return () => {
            cancelled = true;
            stop();
            document.removeEventListener('visibilitychange', onVis);
            window.removeEventListener('focus', ping);
        };
    }, []);
}

// ─── Read presence for a set of users ───────────────────────────────────────
export interface PresenceEntry {
    userId: string;
    lastSeenAt: Date | null;
}

/** Returns Map<userId, Date|null>. Users absent from the map are unknown. */
export function usePresenceFor(userIds: Array<string | null | undefined>) {
    const [presence, setPresence] = useState<Map<string, Date | null>>(new Map());

    // Stable string key for the dependency array
    const ids = userIds.filter((x): x is string => !!x);
    const key = ids.slice().sort().join(',');

    // Keep latest ids in a ref so the interval closure stays fresh
    const idsRef = useRef(ids);
    idsRef.current = ids;

    useEffect(() => {
        if (idsRef.current.length === 0) return;
        let cancelled = false;

        const fetchPresence = async () => {
            const now = idsRef.current;
            if (now.length === 0) return;
            const { data, error } = await supabase.rpc('get_presence', { user_ids: now });
            if (cancelled || error || !data) return;
            const next = new Map<string, Date | null>();
            for (const row of data as Array<{ user_id: string; last_seen_at: string | null }>) {
                next.set(row.user_id, row.last_seen_at ? new Date(row.last_seen_at) : null);
            }
            setPresence(next);
        };

        fetchPresence();
        const id = window.setInterval(fetchPresence, PRESENCE_REFRESH_MS);
        const onFocus = () => fetchPresence();
        window.addEventListener('focus', onFocus);

        return () => {
            cancelled = true;
            window.clearInterval(id);
            window.removeEventListener('focus', onFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return presence;
}

// ─── Formatting ──────────────────────────────────────────────────────────────
export type PresenceTone = 'online' | 'recent' | 'idle' | 'offline' | 'unknown';

export function presenceTone(lastSeen: Date | null | undefined): PresenceTone {
    if (!lastSeen) return 'unknown';
    const diffMs = Date.now() - lastSeen.getTime();
    if (diffMs < ONLINE_WINDOW_MS)              return 'online';   // ≤ 90s
    if (diffMs < 5  * 60_000)                   return 'recent';   // < 5m
    if (diffMs < 60 * 60_000)                   return 'idle';     // < 1h
    return 'offline';
}

/** Compact label: "Online", "5m ago", "2h ago", "3d ago", "Apr 12". */
export function formatPresence(lastSeen: Date | null | undefined): string {
    if (!lastSeen) return 'Offline';
    const diffMs = Date.now() - lastSeen.getTime();
    if (diffMs < ONLINE_WINDOW_MS) return 'Online';

    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;

    return lastSeen.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Verbose label: "Online" | "Last seen 5m ago" — used in headers. */
export function formatPresenceVerbose(lastSeen: Date | null | undefined): string {
    if (!lastSeen) return 'Offline';
    const compact = formatPresence(lastSeen);
    return compact === 'Online' ? compact : `Last seen ${compact}`;
}
